// src/shared/contexts/GlobalAuthContext.tsx
// Standalone version - no Hub dependency

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { projectManagementAPI, User } from '../services/api';
import { sessionManager } from '../services/sessionManager';
import SessionTimeoutModal from '../components/SessionTimeoutModal';

export interface GlobalUser {
  id: number;
  username: string;
  name: string;
  email: string;
  avatar?: string;
  role: 'admin' | 'manager' | 'employee' | 'supervisor' | 'director';
  department_id: number | null;
  department?: string;
  services: string[];
  service_roles: Record<string, string>;
  is_active: boolean;
  employee_id: number | null;
}

interface GlobalAuthContextType {
  user: GlobalUser | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
  hasServiceAccess: (serviceId: string) => boolean;
  refreshUserData: () => Promise<void>;
}

const GlobalAuthContext = createContext<GlobalAuthContextType | undefined>(undefined);

export const useGlobalAuth = (): GlobalAuthContextType => {
  const context = useContext(GlobalAuthContext);
  if (!context) {
    throw new Error('useGlobalAuth must be used within a GlobalAuthProvider');
  }
  return context;
};

interface GlobalAuthProviderProps {
  children: ReactNode;
}

// Convert API User to GlobalUser format
const convertToGlobalUser = (apiUser: User): GlobalUser => ({
  id: apiUser.id,
  username: apiUser.username,
  name: apiUser.name,
  email: apiUser.email,
  role: apiUser.role as GlobalUser['role'],
  department_id: apiUser.department_id,
  services: apiUser.services || [],
  service_roles: apiUser.service_roles || {},
  is_active: apiUser.is_active,
  employee_id: apiUser.employee_id || null
});

export const GlobalAuthProvider: React.FC<GlobalAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<GlobalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);

  // Session management callbacks
  // (initializeSessionManagement é acionado pelo useEffect logo abaixo do
  // checkSession — antes era declarado e nunca chamado, deixando o timeout de
  // inatividade e o SessionTimeoutModal inertes)
  const initializeSessionManagement = () => {
    sessionManager.initialize({
      onWarning: () => {
        const timeRemaining = sessionManager.getTimeRemaining();
        setSessionTimeRemaining(timeRemaining);
        setShowSessionWarning(true);
      },
      onTimeout: () => {
        handleSessionTimeout();
      },
      onExtended: () => {
        setShowSessionWarning(false);
      }
    }, {
      timeout: 60 * 60 * 1000, // 60 minutes
      warningTime: 10 * 60 * 1000, // 10 minutes before timeout
      checkInterval: 60 * 1000 // Check every minute
    });
  };

  const handleSessionTimeout = () => {
    setShowSessionWarning(false);
    performLogout();
    setError('Your session expired due to inactivity. Please log in again.');
  };

  const handleExtendSession = () => {
    sessionManager.extendSession();
    setShowSessionWarning(false);
  };

  const handleManualLogout = () => {
    performLogout();
  };

  // Check for existing session on startup
  useEffect(() => {
    const checkSession = async () => {
      try {
        setLoading(true);

        // Check if we have a stored token
        if (projectManagementAPI.isAuthenticated()) {
          try {
            // Validate token by getting current user
            const apiUser = await projectManagementAPI.getCurrentUser();
            const globalUser = convertToGlobalUser(apiUser);
            setUser(globalUser);
            localStorage.setItem('globalUser', JSON.stringify(globalUser));
          } catch (err) {
            await projectManagementAPI.logout();
            setUser(null);
          }
        } else {
          // Check for stored user data (fallback)
          const storedUser = localStorage.getItem('globalUser');
          if (storedUser) {
            try {
              const cachedUser = JSON.parse(storedUser) as GlobalUser;
              setUser(cachedUser);
            } catch (e) {
              localStorage.removeItem('globalUser');
            }
          }
        }
      } catch (err) {
        setError('Error checking session');
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  // Liga o controle de inatividade enquanto houver usuário autenticado
  useEffect(() => {
    if (!user) return;
    initializeSessionManagement();
    return () => {
      sessionManager.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const login = async (username: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await projectManagementAPI.login({ username, password });

      const globalUser = convertToGlobalUser(response.user);
      setUser(globalUser);
      localStorage.setItem('globalUser', JSON.stringify(globalUser));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const performLogout = async () => {
    // Stop session management
    sessionManager.stop();

    setUser(null);
    setError(null);
    setShowSessionWarning(false);

    try {
      await projectManagementAPI.logout();
    } catch (err) {
      // Silently handle logout errors
    }

    localStorage.removeItem('globalUser');
  };

  const logout = () => {
    performLogout();
  };

  const refreshUserData = async () => {
    if (!projectManagementAPI.isAuthenticated()) return;

    try {
      const apiUser = await projectManagementAPI.getCurrentUser();
      const globalUser = convertToGlobalUser(apiUser);
      setUser(globalUser);
      localStorage.setItem('globalUser', JSON.stringify(globalUser));
    } catch (err) {
      // Silently handle refresh errors
    }
  };

  const hasServiceAccess = (serviceId: string): boolean => {
    if (!user) return false;

    // Map service IDs
    const serviceMapping: Record<string, string> = {
      'people': 'people',
      'management': 'management',
      'nfe': 'nfe',
      'quality-people': 'people',
      'quality-management': 'management',
      'quality-nfe': 'nfe'
    };

    const mappedServiceId = serviceMapping[serviceId] || serviceId;
    return user.services.includes(mappedServiceId);
  };

  const value: GlobalAuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    error,
    hasServiceAccess,
    refreshUserData
  };

  return (
    <GlobalAuthContext.Provider value={value}>
      {children}

      {/* Session timeout warning modal */}
      <SessionTimeoutModal
        isOpen={showSessionWarning}
        timeRemaining={sessionTimeRemaining}
        onExtendSession={handleExtendSession}
        onLogout={handleManualLogout}
      />
    </GlobalAuthContext.Provider>
  );
};
